// apps/buildlog/src/pages/Settings.tsx
import { LogOut, Info, Plug, CheckCircle2, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { getCurrentUser, signOut, auth } from '../services/firebase';

interface ActiveToken {
  id: string;
  issuedAt: string;
  label: string;
}

export function Settings() {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [tokens, setTokens] = useState<ActiveToken[]>([]);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    async function loadTokens() {
      try {
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) return;
        const res = await fetch('https://buildlogapi-x2efogv6ja-uc.a.run.app/settings', {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setTokens(data.activeTokens ?? []);
      } catch {
        // silently fail — connection status is non-critical
      } finally {
        setTokensLoading(false);
      }
    }
    loadTokens();
  }, []);

  const handleRevoke = async (tokenId: string) => {
    if (!window.confirm('Disconnect this connection? Claude will no longer have access to your Buildlog data.')) return;
    setRevoking(tokenId);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      await fetch('https://buildlogapi-x2efogv6ja-uc.a.run.app/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ intent: 'revoke_token', tokenId }),
      });
      setTokens(t => t.filter(tok => tok.id !== tokenId));
    } catch {
      // handle silently
    } finally {
      setRevoking(null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const initials = user?.displayName
    ? user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0].toUpperCase() ?? '?';

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1">Settings</h1>
          <p className="text-slate-500">Manage your account and preferences.</p>
        </div>

        {/* Account */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-900">Account</h2>
          </div>
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {user?.photoURL ? (
                <img src={user.photoURL} alt={user.displayName ?? 'User'} className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-brand-orange/10 text-brand-orange flex items-center justify-center font-bold text-lg border border-brand-orange/20">
                  {initials}
                </div>
              )}
              <div>
                {user?.displayName && (
                  <p className="font-medium text-slate-900">{user.displayName}</p>
                )}
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-brand-slate hover:text-slate-900 transition-colors"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </section>

        {/* Connect Claude */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-900">Connect Claude</h2>
            <p className="text-sm text-slate-500 mt-1">
              Let Claude read your projects and save new entries directly from your conversations.
            </p>
          </div>
          <div className="p-6 space-y-4">

            {/* Active connections */}
            {tokensLoading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                <Loader size={14} className="animate-spin" />
                Checking connections…
              </div>
            ) : tokens.length > 0 ? (
              <div className="space-y-3">
                {tokens.map(tok => (
                  <div key={tok.id} className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{tok.label}</p>
                        <p className="text-xs text-slate-400">
                          Connected {tok.issuedAt ? new Date(tok.issuedAt).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(tok.id)}
                      disabled={revoking === tok.id}
                      className="text-xs font-medium text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
                    >
                      {revoking === tok.id ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                  </div>
                ))}
                <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200">
                  <Info size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-400 leading-relaxed">
                    To add another connection, follow the setup steps below.
                  </p>
                </div>
              </div>
            ) : null}

            {/* Setup instructions — always shown so user can add more connections */}
            <div className="flex items-start gap-4 p-5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm">
                <Plug size={16} className="text-slate-700" />
              </div>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-900">
                  {tokens.length > 0 ? 'Add another connection' : 'How to connect'}
                </p>
                <ol className="space-y-2 text-sm text-slate-500 leading-relaxed list-none">
                  <li className="flex gap-2">
                    <span className="font-mono text-[11px] text-slate-400 mt-0.5 shrink-0">01</span>
                    <span>Open Claude → <span className="font-medium text-slate-700">Settings → Integrations</span></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-mono text-[11px] text-slate-400 mt-0.5 shrink-0">02</span>
                    <span>Click <span className="font-medium text-slate-700">Add custom connector</span></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-mono text-[11px] text-slate-400 mt-0.5 shrink-0">03</span>
                    <span>Paste <code className="font-mono bg-white px-1 py-0.5 rounded border border-slate-200 text-xs">https://buildlog.web.app/mcp</code></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-mono text-[11px] text-slate-400 mt-0.5 shrink-0">04</span>
                    <span>Click <span className="font-medium text-slate-700">Connect</span> and sign in with Google — once only</span>
                  </li>
                </ol>
              </div>
            </div>

            {tokens.length === 0 && !tokensLoading && (
              <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200">
                <Info size={16} className="text-slate-400 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-400 leading-relaxed">
                  Claude can read your projects and save entries on your behalf. You can disconnect at any time from Claude's Integrations settings.
                </p>
              </div>
            )}

          </div>
        </section>

        {/* Import from Claude */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-900">Import from Claude</h2>
            <p className="text-sm text-slate-500 mt-1">
              Bring your Claude.ai conversations into Buildlog as build log entries.
            </p>
          </div>
          <div className="p-6">
            <div className="flex items-start gap-3 p-4 bg-brand-slate rounded-lg border border-slate-200">
              <Info size={18} className="text-slate-400 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-600 space-y-1">
                <p>
                  To import a session: run <code className="font-mono bg-white px-1 py-0.5 rounded border border-slate-200 text-xs">SOP_ARCHIVE</code> in Claude.ai, copy the response, then use <span className="font-medium">Import from Claude</span> inside any project.
                </p>
                <p className="text-slate-400 text-xs">
                  Folder-based import for desktop browsers is coming in a future update.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-900">About</h2>
          </div>
          <div className="p-6 space-y-2 text-sm text-slate-500">
            <div className="flex justify-between">
              <span>Product</span>
              <span className="font-medium text-slate-700">Buildlog</span>
            </div>
            <div className="flex justify-between">
              <span>Part of</span>
              <span className="font-medium text-slate-700">Five Dollar Factory</span>
            </div>
            <div className="flex justify-between">
              <span>Phase</span>
              <span className="font-medium text-slate-700">1 — Core Capture Loop</span>
            </div>
          </div>
        </section>

      </div>
    </AppLayout>
  );
}